import React, { useRef, useEffect, useState } from 'react';
import CanvasMap from '../CanvasMap/CanvasMap';
import { addActivity, saveCanvasStateInChunks } from '../../apiCalls';
import { ACTIVITY_TYPES } from '../../constants';
import { getScreenType } from '../../utils';
import './InteractiveMap.css';
import ExportModal from '../CanvasMap/components/ExportModal';
import Toast from '../Toast/Toast';

const InteractiveMap = ({ 
	anonymousUserId, 
	researchId,
	isEditMode,
	showReuploadControls,
	showResizeControls,
	projectMode,
	setHasUploadedMap,
	onExport
}) => {
	const leftCanvasRef = useRef(null);
	const rightCanvasRef = useRef(null);
	const [leftCanvasReady, setLeftCanvasReady] = useState(false);
	const [rightCanvasReady, setRightCanvasReady] = useState(false);
	const [leftCanvasContent, setLeftCanvasContent] = useState([]);
	const [rightCanvasContent, setRightCanvasContent] = useState([]);
	const [showExportModal, setShowExportModal] = useState(false);
	const [toast, setToast] = useState(null);

	// Keep export functionality
	useEffect(() => {
		const handleExport = (event) => {
			const { option } = event.detail;
			
			// Check if both canvases are ready
			if (!leftCanvasReady || !rightCanvasReady) {
				console.error('Please wait for canvases to be ready');
				return;
			}
			
			// Check if canvases exist
			if (!leftCanvasRef.current || !rightCanvasRef.current) {
				console.error('Canvas references not ready');
				return;
			}
			
			// Create a temporary canvas for flattening
			const tempCanvas = document.createElement('canvas');
			const tempCtx = tempCanvas.getContext('2d');

			if (option === 'both') {
				const width = leftCanvasRef.current.width;
				const height = leftCanvasRef.current.height;
				const margin = 240;  // Add 240px margin on each side
				
				if (!width || !height) {
					console.error('Canvas dimensions not ready');
					return;
				}

				// Set dimensions to fit both canvases vertically with margins
				tempCanvas.width = width + (margin * 2);  // Add left and right margins
				tempCanvas.height = height * 2 + 8;  // Keep vertical spacing at 20px
				
				// Fill with white background
				tempCtx.fillStyle = 'white';
				tempCtx.fillRect(0, 0, width + (margin * 2), height * 2 + 20);
				
				// Draw both canvases vertically with margins
				tempCtx.drawImage(leftCanvasRef.current, margin, 0);  // Add left margin
				tempCtx.drawImage(rightCanvasRef.current, margin, height + 20);  // Add left margin
				
				// Add a subtle separator line (extend it to margins)
				tempCtx.strokeStyle = '#e0e0e0';
				tempCtx.lineWidth = 1;
				tempCtx.beginPath();
				tempCtx.moveTo(0, height + 10);
				tempCtx.lineTo(width + (margin * 2), height + 10);
				tempCtx.stroke();
			} else {
				// Export single canvas
				const sourceCanvas = option === 'left' ? leftCanvasRef.current : rightCanvasRef.current;
				const margin = 240;  // Match the 240px margin from combined export
				
				if (!sourceCanvas.width || !sourceCanvas.height) {
					console.error('Canvas dimensions not ready');
					return;
				}

				tempCanvas.width = sourceCanvas.width + (margin * 2);
				tempCanvas.height = sourceCanvas.height;
				
				// Fill with white background
				tempCtx.fillStyle = 'white';
				tempCtx.fillRect(0, 0, sourceCanvas.width + (margin * 2), sourceCanvas.height);
				
				// Draw the canvas with margins
				tempCtx.drawImage(sourceCanvas, margin, 0);
			}
			
			// Convert to image and trigger download
			const link = document.createElement('a');
			link.download = option === 'both' ? 'canvas-export-both.png' : `canvas-export-${option}.png`;
			link.href = tempCanvas.toDataURL('image/png');
			link.click();
		};

		window.addEventListener('export-canvas', handleExport);
		return () => window.removeEventListener('export-canvas', handleExport);
	}, [leftCanvasReady, rightCanvasReady]);

	// Handle content changes from either canvas
	const handleContentChange = (side) => (content) => {
		if (side === 'left') {
			setLeftCanvasContent(content);
		} else {
			setRightCanvasContent(content);
		}
	};

	const showToast = (message, type = 'success') => {
		setToast({ message, type });
		setTimeout(() => setToast(null), 3000);
	};

	const handleSave = async () => {
		try {
			const dataToSave = {
				leftCanvasContent,
				rightCanvasContent,
				leftImage: leftCanvasRef.current?.toDataURL(),
				rightImage: rightCanvasRef.current?.toDataURL()
			};
			
			await saveCanvasStateInChunks(
				anonymousUserId,
				researchId,
				dataToSave,
				projectMode
			);
			console.log(`✅ Canvas state saved successfully`);
		} catch (error) {
			console.error(`❌ Error saving canvas state:`, error);
		}
	};

	const handleSaveToLibrary = async () => {
		try {
			// Check if both canvases are ready
			if (!leftCanvasReady || !rightCanvasReady) {
				throw new Error('Please wait a moment for the maps to load');
			}
			
			// Check if canvases exist
			if (!leftCanvasRef.current || !rightCanvasRef.current) {
				throw new Error('Unable to find maps to save');
			}

			// Check if maps are empty
			const leftEmpty = !leftCanvasContent || leftCanvasContent.length === 0;
			const rightEmpty = !rightCanvasContent || rightCanvasContent.length === 0;
			if (leftEmpty && rightEmpty) {
				throw new Error('Cannot save empty maps');
			}

			// Create a temporary canvas for the preview (much smaller)
			const tempCanvas = document.createElement('canvas');
			const tempCtx = tempCanvas.getContext('2d');

			const width = leftCanvasRef.current.width;
			const height = leftCanvasRef.current.height;
			const margin = 40;  // Reduced margin for preview
			
			// Scale down the dimensions significantly
			const scale = 0.25;  // 25% of original size
			const scaledWidth = width * scale;
			const scaledHeight = height * scale;
			
			// Set dimensions to fit both canvases vertically with margins
			tempCanvas.width = scaledWidth + (margin * 2);
			tempCanvas.height = (scaledHeight * 2) + 8;
			
			// Fill with white background
			tempCtx.fillStyle = 'white';
			tempCtx.fillRect(0, 0, scaledWidth + (margin * 2), (scaledHeight * 2) + 20);
			
			// Enable image smoothing for better downscaling
			tempCtx.imageSmoothingEnabled = true;
			tempCtx.imageSmoothingQuality = 'high';
			
			// Draw both canvases vertically with margins (scaled down)
			tempCtx.drawImage(leftCanvasRef.current, margin, 0, scaledWidth, scaledHeight);
			tempCtx.drawImage(rightCanvasRef.current, margin, scaledHeight + 20, scaledWidth, scaledHeight);
			
			// Add separator line
			tempCtx.strokeStyle = '#e0e0e0';
			tempCtx.lineWidth = 1;
			tempCtx.beginPath();
			tempCtx.moveTo(0, scaledHeight + 10);
			tempCtx.lineTo(scaledWidth + (margin * 2), scaledHeight + 10);
			tempCtx.stroke();
			
			// Convert to low quality JPEG for smaller size
			const imageData = tempCanvas.toDataURL('image/jpeg', 0.5);  // 50% quality JPEG

			// Check storage quota before saving
			const existingMaps = JSON.parse(localStorage.getItem(`savedMaps_${anonymousUserId}`)) || [];
			const newMapSize = imageData.length * 2;  // Rough estimate in bytes
			
			try {
				// Test if we can store this
				localStorage.setItem('quotaTest', 'x'.repeat(newMapSize));
				localStorage.removeItem('quotaTest');
			} catch (e) {
				if (e.name === 'QuotaExceededError') {
					throw new Error('storage-full');
				}
			}

			// If we got here, we can save
			existingMaps.push({
				imageData,
				savedAt: new Date().toISOString()
			});
			localStorage.setItem(`savedMaps_${anonymousUserId}`, JSON.stringify(existingMaps));

			showToast('Map saved to library');
		} catch (error) {
			console.error('Failed to save map:', error);
			
			// Map technical errors to user-friendly messages
			const userFriendlyMessage = {
				'Please wait a moment for the maps to load': error.message,
				'Unable to find maps to save': error.message,
				'Cannot save empty maps': error.message,
				'Canvas references not ready': 'Unable to save maps right now',
				'storage-full': 'Library is full. Please delete some maps to save new ones.',
				'default': 'Unable to save maps. Please try again'
			}[error.message] || 'Unable to save maps. Please try again';

			showToast(userFriendlyMessage, 'error');
		}
	};

	const handleExport = (option) => {
		const event = new CustomEvent('export-canvas', { 
			detail: { option } 
		});
		window.dispatchEvent(event);
		setShowExportModal(false);
	};

	return (
		<>
			<div style={{
				width: "100%",
				height: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				gap: "1rem"
			}}>
				<CanvasMap 
					ref={leftCanvasRef}
					width="50vw" 
					height="100%" 
					onCanvasReady={() => setLeftCanvasReady(true)}
					onContentChange={handleContentChange('left')}
					isEditMode={isEditMode}
					showReuploadControls={showReuploadControls}
					showResizeControls={showResizeControls}
					setHasUploadedMap={setHasUploadedMap}
					onExport={onExport}
				/>
				<CanvasMap 
					ref={rightCanvasRef}
					width="50%" 
					height="100%" 
					onCanvasReady={() => setRightCanvasReady(true)}
					onContentChange={handleContentChange('right')}
					isEditMode={isEditMode}
					showReuploadControls={showReuploadControls}
					showResizeControls={showResizeControls}
				/>
			</div>
			<div style={{
				position: 'fixed',
				bottom: '20px',
				right: '20px',
				display: 'flex',
				gap: '10px'
			}}>
				<button
					onClick={() => setShowExportModal(true)}
					style={{
						backgroundColor: '#343a40',
						color: 'white',
						border: 'none',
						padding: '10px 20px',
						borderRadius: '20px',
						cursor: 'pointer',
						boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						fontSize: '14px'
					}}
				>
					Export
				</button>
				<button
					onClick={handleSaveToLibrary}
					style={{
						backgroundColor: '#343a40',
						color: 'white',
						border: 'none',
						padding: '10px 20px',
						borderRadius: '20px',
						cursor: 'pointer',
						boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						fontSize: '14px'
					}}
				>
					Save to Library
				</button>
			</div>
			{showExportModal && (
				<ExportModal
					onClose={() => setShowExportModal(false)}
					onExport={handleExport}
					anonymousUserId={anonymousUserId}
				/>
			)}
			{toast && (
				<Toast 
					message={toast.message}
					type={toast.type}
				/>
			)}
		</>
	);
};

export default InteractiveMap;